'use client';

import React, { useEffect, useRef } from 'react';

interface StepItem {
  word1?: string;
  word2?: string;
}

interface CustomizeGlassesData {
  title?: string;
  step_arr?: StepItem[];
}

interface StepBoxProps {
  stepData: CustomizeGlassesData;
  isMobile: boolean;
  onExposure?: (mkName: string) => void;
}

const StepBox: React.FC<StepBoxProps> = ({ stepData, isMobile, onExposure }) => {
  const stepBoxRef = useRef<HTMLDivElement>(null);
  const hasTriggeredExposure = useRef(false);

  // 使用Intersection Observer检测曝光
  useEffect(() => {
    if (!stepBoxRef.current || !onExposure) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTriggeredExposure.current) {
          hasTriggeredExposure.current = true;
          onExposure('驾驶镜优势3');
        }
      },
      { threshold: 0.3 }, // 30%可见时触发
    );

    observer.observe(stepBoxRef.current);

    return () => {
      observer.disconnect();
    };
  }, [onExposure]);

  if (!stepData.step_arr || stepData.step_arr.length === 0) {
    return null;
  }

  return (
    <div
      ref={stepBoxRef}
      className={`step-box ${isMobile ? 'bg-[#f5f6f7]' : 'bg-[#ffffff]'} text-primary-black ${
        isMobile
          ? 'pt-[36px] px-[8px] pb-[30px]'
          : 'pt-[40px] px-[80px] pb-[15px] xl:pt-[60px] xl:px-[100px] xl:pb-[20px] 2xl:pt-[80px] 2xl:px-[120px] 2xl:pb-[30px]'
      }`}
    >
      <h2
        className={`title text-center font-bb ${
          isMobile
            ? 'text-[20px] leading-[30px] mb-[20px]'
            : 'text-[24px] mb-[10px] xl:text-[36px] xl:mb-[20px] 2xl:text-[48px] 2xl:mb-[30px]'
        }`}
      >
        {stepData.title}
      </h2>

      {isMobile ? (
        // 移动端布局 - 参考 StepArr.vue
        <ul className="list-none p-0 m-0 rounded-md border border-[#89909a] p-[16px]">
          {stepData.step_arr?.map((item, index) => (
            <li key={index} className="pb-5 last:pb-0">
              <span className="font-bs text-[13px]">{item.word1}&nbsp;:&nbsp;&nbsp;</span>
              <span className="text-[11px]">{item.word2}</span>
            </li>
          ))}
        </ul>
      ) : (
        // PC端布局 - 参考 DrivingBenefit.vue
        <ul className="list-none p-0 m-0 gap-x-[20px] grid grid-cols-3 text-primary-black">
          {stepData.step_arr?.map((item, index) => (
            <li
              key={index}
              className="flex items-center justify-center w-full h-[60px] xl:h-[70px] 2xl:h-[80px] bg-[#f5f6f7] rounded-xl px-[6px] xl:px-[8px] 2xl:px-[10px]"
            >
              <span className="font-bs text-[14px] xl:text-[18px] 2xl:text-[24px] min-w-[fit-content]">
                {item.word1}&nbsp;:&nbsp;&nbsp;
              </span>
              <span className="text-[12px] xl:text-[14px] 2xl:text-[16px]">{item.word2}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default StepBox;
